import { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, AlertCircle, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import ApiService from '../../../services/api'
import VIPRoomsModal from '../../../components/modals/VIPRoomsModal'
import { VIPRoom, initialVIPRoomFormData } from '../../../models/VIPRoom'
import AuthService from '../../../services/authService'

const VIPRooms = () => {
  const [vipRooms, setVipRooms] = useState([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingRoom, setEditingRoom] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [branches, setBranches] = useState([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState(initialVIPRoomFormData)
  const user = AuthService.getCurrentUser()

  // Helper function to get user's branch ID for filtering
  const getUserBranchId = () => {
    if (!user) return null
    
    // For super_admin, admin, or owner - can see all branches
    const unrestricted = ['super_admin', 'admin', 'owner']
    if (unrestricted.includes(user.role?.toLowerCase())) {
      return null
    }
    
    // For manager and other roles - only their branch
    return user.branchId || user.branch?._id || user.branch || null
  }

  // Fetch VIP Rooms
  const fetchVIPRooms = async () => {
    setLoading(true)
    try {
      const branchId = getUserBranchId()
      const params = branchId ? `?page=${page}&limit=10&branch=${branchId}` : `?page=${page}&limit=10`
      const response = await ApiService.get(`/vip-rooms${params}`)
      if (response.success) {
        // Ensure we always set an array - check for 'rooms' first
        const rooms = response.data?.rooms || response.data?.vipRooms || response.data?.data || []
        setVipRooms(Array.isArray(rooms) ? rooms : [])
        setTotalPages(response.data?.totalPages || 1)
      }
    } catch (error) {
      console.error('Error fetching VIP Rooms:', error)
      setVipRooms([]) // Set empty array on error
    } finally {
      setLoading(false)
    }
  }

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const response = await ApiService.get('/branches?isActive=true')
      if (response.success) {
        let branchList = response.data?.branches || response.data?.data || []
        branchList = Array.isArray(branchList) ? branchList : []
        
        // Filter branches for managers - only show their branch
        const branchId = getUserBranchId()
        if (branchId) {
          branchList = branchList.filter(b => b._id === branchId)
        }
        
        setBranches(branchList)
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
      setBranches([]) // Set empty array on error
    }
  }

  useEffect(() => {
    fetchVIPRooms()
    fetchBranches()
  }, [page])

  // Validate form
  const validateForm = () => {
    const room = new VIPRoom(formData)
    const validation = room.validate()
    setErrors(validation.errors)
    return validation.isValid
  }

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    const room = new VIPRoom(formData)
    const payload = room.toPayload()

    try {
      if (editingRoom) {
        // Update
        const response = await ApiService.patch(`/vip-rooms/${editingRoom._id}`, payload)
        if (response.success) {
          const updatedRoom = VIPRoom.fromApiResponse(response.data.vipRoom)
          setVipRooms(vipRooms.map(r => 
            r._id === editingRoom._id ? updatedRoom : r
          ))
          toast.success(response.message || 'VIP Room updated successfully')
          handleCloseModal()
        }
      } else {
        // Create
        const response = await ApiService.post('/vip-rooms', payload)
        if (response.success) {
          const newRoom = VIPRoom.fromApiResponse(response.data.vipRoom)
          setVipRooms([newRoom, ...vipRooms])
          toast.success(response.message || 'VIP Room created successfully')
          handleCloseModal()
        }
      }
    } catch (error) {
      console.error('Error saving VIP Room:', error)
      toast.error(error.message || 'Failed to save VIP Room')
      setErrors({ submit: error.message || 'Failed to save VIP Room' })
    }
  }

  // Handle edit
  const handleEdit = (room) => {
    setEditingRoom(room)
    setFormData({
      name: room.name,
      pricePerHour: room.pricePerHour,
      branch: room.branch._id || room.branch,
      category: room.category,
      discount: room.discount,
      isActive: room.isActive
    })
    setShowModal(true)
  }

  // Handle delete
  const handleDelete = async () => {
    if (!deleteConfirm) return
    
    try {
      const response = await ApiService.delete(`/vip-rooms/${deleteConfirm._id}`)
      // Check for success or 204 No Content status (successful deletion)
      if (response.success || response.status === 204 || response.statusCode === 204 || !response) {
        setVipRooms(vipRooms.filter(room => room._id !== deleteConfirm._id))
        toast.success('VIP Room deleted successfully')
        setDeleteConfirm(null)
      }
    } catch (error) {
      console.error('Error deleting VIP Room:', error)
      toast.error(error.message || 'Failed to delete VIP Room')
    }
  }

  // Reset form
  const resetForm = () => {
    setFormData(initialVIPRoomFormData)
    setErrors({})
    setEditingRoom(null)
  }

  // Close modal and reset
  const handleCloseModal = () => {
    setShowModal(false)
    resetForm()
  }

  // Open modal for creating new room
  const handleCreateNew = () => {
    resetForm()
    const branchId = getUserBranchId()
    if (branchId) {
      // Auto-set branch for managers
      setFormData(prev => ({ ...prev, branch: branchId }))
    }
    setShowModal(true)
  }

  // Handle refresh
  const handleRefresh = () => {
    fetchVIPRooms()
    toast.success('VIP Rooms refreshed')
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">VIP Rooms Management</h1>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleCreateNew}
            className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Add VIP Room</span>
          </button>
        </div>
      </div>

      {/* VIP Rooms Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            <span className="ml-3 text-gray-600">Loading VIP Rooms...</span>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          {/* Desktop View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ROOM NAME
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    BRANCH
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CATEGORY
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PRICE/HOUR
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DISCOUNT
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    STATUS
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vipRooms.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No VIP Rooms found. Click "Add VIP Room" to create your first room.
                    </td>
                  </tr>
                ) : (
                  vipRooms.map(roomData => {
                    const room = new VIPRoom(roomData)
                    return (
                      <tr key={room._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{room.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{room.getBranchName()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${room.getCategoryBadgeColor()}`}>
                            {room.getFormattedCategory()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{room.getFormattedPrice()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{room.discount}%</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${room.getStatusBadgeColor()}`}>
                            {room.getStatusText()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleEdit(roomData)}
                              className="text-gray-600 hover:text-gray-900 transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(roomData)}
                              className="text-gray-600 hover:text-gray-900 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-4 p-4">
          {vipRooms.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No VIP Rooms found. Click "Add VIP Room" to create your first room.
            </div>
          ) : (
            vipRooms.map(roomData => {
              const room = new VIPRoom(roomData)
              return (
                <div key={room._id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">{room.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{room.getBranchName()}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(roomData)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(roomData)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Category:</span>
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${room.getCategoryBadgeColor()}`}>
                        {room.getFormattedCategory()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Price/Hour:</span>
                      <span className="text-sm font-semibold text-gray-900">{room.getFormattedPrice()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Discount:</span>
                      <span className="text-sm text-gray-900">{room.discount}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Status:</span>
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${room.getStatusBadgeColor()}`}>
                        {room.getStatusText()}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2 p-4 border-t border-gray-200">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
      )}

      {/* VIP Rooms Modal */}
      <VIPRoomsModal
        isOpen={showModal}
        onClose={handleCloseModal}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmit}
        isEditing={!!editingRoom}
        branches={branches}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-sm w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Delete VIP Room</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VIPRooms
